import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import storeLogos from "../../assets/images/brand-logos.svg"
import "./Campaigns.scss"

const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// SVG sprite: brand-logos.svg — 555×12 px — brand x-positions (px) and widths
const BRAND_LOGO_MAP = {
  "H&M": { x: 0, w: 23 },
  COS: { x: 31, w: 40 },
  Weekday: { x: 79, w: 69 },
  WEEKDAY: { x: 79, w: 69 },
  "H&M Home": { x: 155, w: 66 },
  "H&M HOME": { x: 155, w: 66 },
  "& Other Stories": { x: 229, w: 35 },
  ARKET: { x: 272, w: 62 },
  "Cheap Monday": { x: 342, w: 29 },
  "CHEAP MONDAY": { x: 342, w: 29 },
  Monki: { x: 378, w: 69 },
  MONKI: { x: 378, w: 69 },
  "Singular Society": { x: 455, w: 50 },
  "SINGULAR SOCIETY": { x: 455, w: 50 },
  Sellpy: { x: 513, w: 44 },
  SELLPY: { x: 513, w: 44 },
}

const LOGO_SCALE = 1.3
const SVG_NATIVE_W = 555
const SVG_NATIVE_H = 12

const BrandLogo = ({ brand }) => {
  if (!brand) return null
  const config = BRAND_LOGO_MAP[brand]
  if (!config) return <span className="cam-item-brand">{brand}</span>

  const displayW = Math.round(config.w * LOGO_SCALE)
  const displayH = Math.round(SVG_NATIVE_H * LOGO_SCALE)
  const bgW = Math.round(SVG_NATIVE_W * LOGO_SCALE)
  const bgX = -Math.round(config.x * LOGO_SCALE)

  return (
    <span
      className="cam-brand-logo"
      role="img"
      aria-label={brand}
      style={{
        width: displayW,
        height: displayH,
        backgroundImage: `url(${storeLogos})`,
        backgroundSize: `${bgW}px ${displayH}px`,
        backgroundPosition: `${bgX}px 0`,
      }}
    />
  )
}

BrandLogo.propTypes = { brand: PropTypes.string }

// The API may send the domain as its own field or only as the row category.
const GLOBAL_DOMAIN_PATTERN = /^\s*(global|general)(\s*\/\s*(global|general))?\s*$/i

const isGlobalItem = (item) =>
  GLOBAL_DOMAIN_PATTERN.test(
    String(item.domainUnit || item.domain_unit || item.domain || item.category || ""),
  )

const getItemKind = (item) => (item.type === "campaign" ? "campaign" : "activity")

const LEGEND_ITEMS = [
  { key: "global-campaign", label: "Global Campaigns" },
  { key: "global-activity", label: "Global Activities" },
  { key: "campaign", label: "Other Campaigns" },
  { key: "activity", label: "Other Activities" },
]

const buildDateStr = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`

/** Add n days to a local date string */
const addDaysToDateStr = (dateStr, n) => {
  const [y, m, d] = dateStr.split("-").map(Number)
  const date = new Date(y, m - 1, d)
  date.setDate(date.getDate() + n)
  return buildDateStr(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

const MONTH_ABBR = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
]

/** Format YYYY-MM-DD → DD-MON-YYYY e.g. 27-JUN-2026 */
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "—"
  const [y, m, d] = dateStr.split("-").map(Number)
  return `${String(d).padStart(2, "0")}-${MONTH_ABBR[m - 1]}-${y}`
}

/** Greedy lane packing: returns array of lanes (each lane = array of items), sorted by colStart */
const computeLanes = (items) => {
  if (!items.length) return []
  const sorted = [...items].sort((a, b) => a.colStart - b.colStart)
  const lanes = []
  sorted.forEach((item) => {
    let placed = false
    for (const lane of lanes) {
      const last = lane[lane.length - 1]
      if (last.colStart + last.colSpan <= item.colStart) {
        lane.push(item)
        placed = true
        break
      }
    }
    if (!placed) lanes.push([item])
  })
  return lanes
}

const CampaignItem = ({ item, rowNum, onSelect }) => (
  <button
    type="button"
    className={`cam-item cam-item-${item.type}${isGlobalItem(item) ? ` cam-item-global-${getItemKind(item)}` : ""}`}
    style={{
      gridColumn: `${item.colStart + 2} / span ${item.colSpan}`,
      gridRow: rowNum,
    }}
    title={item.brand ? `${item.name} (${item.brand})` : item.name}
    onClick={() => onSelect(item)}
  >
    <div className="cam-item-content">
      <span className="cam-item-name">{item.name}</span>
      <BrandLogo brand={item.brand} />
    </div>
  </button>
)

CampaignItem.propTypes = {
  item: PropTypes.object.isRequired,
  rowNum: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
}

const formatLabelAsIs = (label) => label

/** Detail popup for a campaign/activity; also used by the Upcoming Campaigns table's view action. */
export const CampaignDetailDialog = ({ item, onClose, formatCategoryLabel = formatLabelAsIs }) => {
  const isGlobal = Boolean(item) && isGlobalItem(item)

  return (
    <Dialog
      open={Boolean(item)}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { className: "cam-dialog-paper" } }}
    >
      {item && (
        <>
          <DialogTitle className="cam-dialog-title">
            <span
              className={`cam-dialog-type-badge cam-dialog-type-${item.type}${isGlobal ? ` cam-dialog-type-global-${getItemKind(item)}` : ""}`}
            >
              {isGlobal ? "Global " : ""}
              {item.type === "campaign" ? "Campaign" : "Activity"}
            </span>
            <IconButton aria-label="close" onClick={onClose} className="cam-dialog-close" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent className="cam-dialog-content">
            <div className="cam-view-field">
              <span className="cam-view-label">Name</span>
              <span className="cam-view-value">{item.name}</span>
            </div>
            <div className="cam-view-field">
              <span className="cam-view-label">Category</span>
              <span className="cam-view-value">{item.category ? formatCategoryLabel(item.category) : "—"}</span>
            </div>
            {item.type === "campaign" && (
              <div className="cam-view-field">
                <span className="cam-view-label">Brand</span>
                <span className="cam-view-value cam-view-brand">
                  {item.brand ? <BrandLogo brand={item.brand} /> : "—"}
                </span>
              </div>
            )}
            <div className="cam-view-row">
              <div className="cam-view-field">
                <span className="cam-view-label">Start Date</span>
                <span className="cam-view-value">{item.startDate}</span>
              </div>
              <div className="cam-view-field">
                <span className="cam-view-label">End Date</span>
                <span className="cam-view-value">{item.endDate}</span>
              </div>
            </div>
            <div className="cam-view-field">
              <span className="cam-view-label">Comments</span>
              <div
                className="cam-view-value cam-view-comments"
                dangerouslySetInnerHTML={{ __html: item.comments || "—" }}
              />
            </div>
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}

CampaignDetailDialog.propTypes = {
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  formatCategoryLabel: PropTypes.func,
}

const Campaigns = ({ data, isLoading, formatCategoryLabel = formatLabelAsIs }) => {
  const calendarData = useMemo(() => data || {}, [data])
  const [selectedItem, setSelectedItem] = useState(null)

  const { weekGridCols, allDates, categories, categoryLanes, categoryRowStarts, categoryNumLanes } =
    useMemo(() => {
      const weeks = calendarData.dateRange?.weeks || []
      const cats = calendarData.categories || []
      const items = calendarData.items || []

      // Build flat list of all dates across all weeks
      const allDates = []
      weeks.forEach((week, wIdx) => {
        const isLastWeek = wIdx === weeks.length - 1
        week.days.forEach((dayNum, i) => {
          const isLastDayOfWeek = i === week.days.length - 1
          const dateStr = addDaysToDateStr(week.start, i)
          const [y, m, d] = dateStr.split("-").map(Number)
          const jsDay = new Date(y, m - 1, d).getDay()
          allDates.push({
            dateStr,
            dayNum,
            dayName: DAY_NAMES_SHORT[jsDay],
            weekNumber: week.weekNumber,
            isWeekEnd: isLastDayOfWeek && !isLastWeek,
          })
        })
      })

      const totalDays = allDates.length

      // Compute week header grid column positions
      let cursor = 2 // col 1 = category label; days start at col 2
      const weekGridCols = weeks.map((week) => {
        const gridColStart = cursor
        cursor += week.days.length
        return { ...week, gridColStart, gridColSpan: week.days.length }
      })

      // Map each item to grid column positions (0-indexed into allDates)
      const getItemPosition = (item) => {
        const startIdx = allDates.findIndex((d) => d.dateStr >= item.startDate)
        if (startIdx === -1) return null

        let endIdx = -1
        for (let i = totalDays - 1; i >= 0; i--) {
          if (allDates[i].dateStr <= item.endDate) {
            endIdx = i
            break
          }
        }
        if (endIdx === -1 || startIdx > endIdx) return null

        return { colStart: startIdx, colSpan: endIdx - startIdx + 1 }
      }

      const itemsWithPos = items
        .filter((item) => item.id?.trim() && item.name && item.startDate && item.endDate)
        .map((item) => {
          const pos = getItemPosition(item)
          if (!pos) return null
          return { ...item, ...pos }
        })
        .filter(Boolean)

      // Compute lanes per category
      const categoryLanes = {}
      cats.forEach((cat) => {
        const catItems = itemsWithPos.filter((i) => i.category === cat)
        categoryLanes[cat] = computeLanes(catItems)
      })

      // Compute grid row start per category (rows 1 & 2 = headers, rows 3+ = data)
      let currentRow = 3
      const categoryRowStarts = {}
      const categoryNumLanes = {}
      cats.forEach((cat) => {
        categoryRowStarts[cat] = currentRow
        const numLanes = Math.max((categoryLanes[cat] || []).length, 1)
        categoryNumLanes[cat] = numLanes
        currentRow += numLanes
      })

      return {
        weekGridCols,
        allDates,
        categories: cats,
        categoryLanes,
        categoryRowStarts,
        categoryNumLanes,
      }
    }, [calendarData])

  const totalDays = allDates.length
  const numWeeks = weekGridCols.length

  const gridStyle = {
    // Wider day-column minimum so each week's full header label (e.g. "WEEK 39 (September 21 - 27)")
    // has enough room and doesn't get clipped/overlap the next week.
    gridTemplateColumns: `200px repeat(${totalDays}, minmax(45px, 1fr))`,
  }

  return (
    <div className="cam-wrapper">
      <div className="cam-title-row">
        <h3 className="cam-title">UPCOMING CAMPAIGNS &amp; ACTIVITIES</h3>
        <div className="cam-legend">
          {LEGEND_ITEMS.map(({ key, label }) => (
            <React.Fragment key={key}>
              <span className={`cam-legend-dot cam-legend-${key}`} />
              <span className="cam-legend-label">{label}</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="cam-loading" role="status" aria-live="polite">
          <CircularProgress size={28} sx={{ color: "#111111" }} />
          <span>Loading</span>
        </div>
      ) : (
        <div className="cam-scroll-container">
          <div className="cam-grid" style={gridStyle}>
            {/* ── Row 1: corner cell + week headers ── */}
            <div className="cam-corner-cell" style={{ gridRow: "1 / span 2", gridColumn: 1 }}>
              CATEGORY
              <br />/ WEEK
            </div>

            {weekGridCols.map((week) => (
              <div
                key={week.weekNumber}
                className="cam-week-header"
                style={{
                  gridColumn: `${week.gridColStart} / span ${week.gridColSpan}`,
                  gridRow: 1,
                }}
              >
                <span className="cam-week-label">{week.label}</span>
                <span className="cam-week-range">{week.monthLabel}</span>
              </div>
            ))}

            {/* ── Row 2: day name + day number headers ── */}
            {allDates.map((d, i) => (
              <div
                key={`dh-${d.dateStr}`}
                className={`cam-day-header${d.isWeekEnd ? " cam-week-end" : ""}`}
                style={{ gridColumn: i + 2, gridRow: 2 }}
              >
                <div className="cam-day-name">{d.dayName}</div>
                <div className="cam-day-num">{d.dayNum}</div>
              </div>
            ))}

            {/* ── Rows 3+: category rows ── */}
            {categories.map((cat, catIdx) => {
              const lanes = categoryLanes[cat] || []
              const rowStart = categoryRowStarts[cat]
              const numLanes = categoryNumLanes[cat]
              const isEven = catIdx % 2 === 0

              return (
                <React.Fragment key={cat}>
                  {/* Category label spanning all its lanes */}
                  <div
                    className={`cam-cat-label ${isEven ? "cam-row-even" : "cam-row-odd"}`}
                    style={{ gridColumn: 1, gridRow: `${rowStart} / span ${numLanes}` }}
                  >
                    {formatCategoryLabel(cat)}
                  </div>

                  {/* Per-lane: day background cells + items */}
                  {Array.from({ length: numLanes }, (_, laneIdx) => {
                    const lane = lanes[laneIdx] || []
                    const rowNum = rowStart + laneIdx

                    return (
                      <React.Fragment key={`lane-${cat}-${laneIdx}`}>
                        {allDates.map((d, dayIdx) => (
                          <div
                            key={`cell-${cat}-${laneIdx}-${dayIdx}`}
                            className={`cam-body-cell${d.isWeekEnd ? " cam-week-end" : ""}`}
                            style={{ gridColumn: dayIdx + 2, gridRow: rowNum }}
                          />
                        ))}

                        {lane.map((item) => (
                          <CampaignItem
                            key={item.id}
                            item={item}
                            rowNum={rowNum}
                            onSelect={setSelectedItem}
                          />
                        ))}
                      </React.Fragment>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Campaign / Activity detail popup ── */}
      <CampaignDetailDialog
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        formatCategoryLabel={formatCategoryLabel}
      />
    </div>
  )
}

Campaigns.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  formatCategoryLabel: PropTypes.func,
}

Campaigns.defaultProps = {
  data: null,
  isLoading: false,
}

export default Campaigns
